import { isoZ, z } from 'my-zod'

/**
 * Full schema for outfitter rental configuration
 * Represents all configuration settings related to the outfitter’s rental operations.
 */
const outfitterRentalConfiguration = z.object({
  outfitterId: z.number(),
  reservationVehicleAvailabilityTurnAroundMinutes: z.number().int().nonnegative(),
  vehiclePrepExpirationDays: z.number().int().min(1, 'Must be at least 1 day').max(30, 'Cannot exceed 30 days'),
  allowVehicleCheckoutToTriggerTransfer: isoZ.instant().nullable(),
  giftCardsEnabledDate: isoZ.instant().nullable(),
  giftCardTermsAndConditions: z.string().nullable(),
  isAbandonedCartEmailEnabled: isoZ.instant().nullable()
})

/**
 * Schema for updating only the reservation vehicle availability turnaround time.
 */
const reservationVehicleAvailabilityTurnAroundMinutes = outfitterRentalConfiguration.pick({
  reservationVehicleAvailabilityTurnAroundMinutes: true
})

/**
 * Schema for updating only the vehicle preparation expiration days.
 */
const vehiclePrepExpirationDays = outfitterRentalConfiguration.pick({ vehiclePrepExpirationDays: true })

/**
 * Schema for updating only the vehicle check-outs trigger transfers.
 */
const allowVehicleCheckoutToTriggerTransfer = z.object({
  allowVehicleCheckoutToTriggerTransfer: z.boolean()
})

/**
 * Schema for updating only the gift card terms and conditions.
 */
const giftCardTermsAndConditions = outfitterRentalConfiguration.pick({ giftCardTermsAndConditions: true })

/**
 * Schema for updating only the abandoned cart email enabled date.
 */
const isAbandonedCartEmailEnabled = z.object({
  isAbandonedCartEmailEnabled: z.boolean()
})

export const OutfitterRentalConfigurationSchemas = {
  outfitterRentalConfiguration,
  reservationVehicleAvailabilityTurnAroundMinutes,
  vehiclePrepExpirationDays,
  allowVehicleCheckoutToTriggerTransfer,
  giftCardTermsAndConditions,
  isAbandonedCartEmailEnabled
}

export declare namespace OutfitterRentalConfigurationSchemas {
  export type outfitterRentalConfiguration = z.infer<typeof outfitterRentalConfiguration>
  export type vehiclePrepExpirationDays = z.infer<typeof vehiclePrepExpirationDays>
  export type reservationVehicleAvailabilityTurnAroundMinutes = z.infer<
    typeof reservationVehicleAvailabilityTurnAroundMinutes
  >
  export type giftCardTermsAndConditions = z.infer<typeof giftCardTermsAndConditions>
  export type isAbandonedCartEmailEnabled = z.infer<typeof isAbandonedCartEmailEnabled>
}
